import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getDisplayName } from '../../utils/admin';
import { LS } from '../../utils/storage';
import { useAvatarUrl } from '../../utils/avatars';
import { useConfirm } from '../ConfirmModal';
import { Icon } from '../Icon';
import { Popover, Switch } from './Popover';
import { UI_SCALE_STEPS, effectiveUiScale, getUiScalePref, setUiScalePref } from '../../utils/uiScale';

const isDark = () => document.body.classList.contains('dark-mode');

// 화면 크기: − / + 로 한 단계씩, 가운데 글자를 누르면 자동(창 너비에 맞춤)으로
function UiScaleControl() {
    const [pref, setPref] = useState(getUiScalePref);
    const current = effectiveUiScale(pref);
    const idx = UI_SCALE_STEPS.indexOf(current);
    const change = (next) => { setUiScalePref(next); setPref(next); };
    return (
        <div className="menu-item ui-scale-row">
            <Icon name="display" size={18} />
            <span>화면 크기</span>
            <div className="ui-scale-control">
                <button type="button" aria-label="작게" disabled={idx <= 0} onClick={() => change(UI_SCALE_STEPS[idx - 1])}>−</button>
                <button type="button" className="ui-scale-value" title="자동으로 (창 크기에 맞춤)" onClick={() => change('auto')}>
                    {pref === 'auto' ? `자동 ${Math.round(current * 100)}%` : `${Math.round(current * 100)}%`}
                </button>
                <button type="button" aria-label="크게" disabled={idx >= UI_SCALE_STEPS.length - 1} onClick={() => change(UI_SCALE_STEPS[idx + 1])}>+</button>
            </div>
        </div>
    );
}

// 오른쪽 끝 내 프로필 버튼 → 계정 메뉴 (계정 설정 · 알림·소리 · 다크 모드 · 로그아웃)
// 로그아웃·테마 전환이 상단 바에 바로 있으면 잘못 누르기 쉽고, 모바일에서 아이콘이 너무 많아져서 한곳에 모았다.
function UserMenu({ user, account, settings }) {
    const { signOut } = useAuth();
    const { confirm, modal } = useConfirm();
    const avatar = useAvatarUrl(user?.userId);
    const [dark, setDark] = useState(isDark);
    const [view, setView] = useState('main');
    const name = getDisplayName(user?.username);

    const toggleTheme = (next) => {
        document.body.classList.toggle('dark-mode', next);
        LS.set('dark-mode', next);
        setDark(next);
    };
    const logout = async (close) => {
        close();
        if (await confirm('로그아웃할까요?')) signOut();
    };

    return (
        <>
            {modal}
            <Popover className="user-menu" button={({ open, toggle }) => (
                <button className={`nav-profile-btn${open ? ' active' : ''}`} onClick={() => { setView('main'); toggle(); }}
                    title="내 계정" aria-label="내 계정 메뉴" aria-expanded={open}>
                    {avatar ? <img className="nav-avatar" src={avatar} alt="" /> : <span className="nav-avatar placeholder"><Icon name="user" size={16} /></span>}
                    <span className="site-nav-name">{name}</span>
                </button>
            )}>
                {({ close }) => (view === 'settings' ? (
                    <>
                        <button className="menu-back" onClick={() => setView('main')}><Icon name="chevronLeft" size={18} />알림·소리</button>
                        {settings}
                    </>
                ) : (
                    <>
                        <div className="user-menu-head">
                            {avatar ? <img className="user-menu-avatar" src={avatar} alt="" /> : <span className="user-menu-avatar placeholder"><Icon name="user" size={22} /></span>}
                            <div>
                                <b>{name}</b>
                                <small>{user?.loginId || '게스트'}</small>
                            </div>
                        </div>
                        {account && (
                            <button className="menu-item" onClick={() => { close(); account.onOpen(); }}>
                                <Icon name="user" size={18} /><span>{account.label}</span>
                            </button>
                        )}
                        {settings && (
                            <button className="menu-item" onClick={() => setView('settings')}>
                                <Icon name="bell" size={18} /><span>알림·소리</span><Icon name="chevronRight" size={16} className="menu-item-end" />
                            </button>
                        )}
                        <div className="menu-item switch">
                            <Icon name={dark ? 'moon' : 'sun'} size={18} />
                            <Switch label="다크 모드" checked={dark} onChange={toggleTheme} />
                        </div>
                        <UiScaleControl />
                        <div className="menu-divider" />
                        <button className="menu-item danger" onClick={() => logout(close)}>
                            <Icon name="logout" size={18} /><span>로그아웃</span>
                        </button>
                    </>
                ))}
            </Popover>
        </>
    );
}

// 채팅·다운로드·관리자 공통 상단 바
// leading: 모바일 채널 목록 버튼 등 왼쪽 끝 요소 / actions: 화면별 추가 버튼 (채팅의 멘션함·접속자)
// account: { label, onOpen } 계정 설정 열기 / settings: 계정 메뉴 안 "알림·소리" 화면 내용
export function SiteNav({ leading, actions, account, settings }) {
    const { user } = useAuth();
    return (
        <nav className="site-nav">
            {leading}
            <Link to="/" className="site-nav-brand">Break Chat</Link>
            <div className="site-nav-links">
                <NavLink to="/" end title="채팅"><Icon name="chat" size={18} /><span className="nav-label">채팅</span></NavLink>
                <NavLink to="/download" title="다운로드"><Icon name="download" size={18} /><span className="nav-label">다운로드</span></NavLink>
                {user?.isAdmin && <NavLink to="/admin" title="관리자"><Icon name="shield" size={18} /><span className="nav-label">관리자</span></NavLink>}
            </div>
            <div className="site-nav-actions">{actions}</div>
            <UserMenu user={user} account={account} settings={settings} />
        </nav>
    );
}
